import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkle, ShieldCheck, Users, Books, ChartLineUp, Percent, FloppyDisk, ArrowLeft, PencilSimple, Trash, Plus, X, Wrench, ArrowFatLineUp, CaretDown, CaretRight, CaretLeft, MagnifyingGlass, Medal, CurrencyDollar, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, clearAuth } from "@/lib/api";
import UsageOverview from "@/components/UsageOverview";
import IntegrityChecksPanel from "@/components/IntegrityChecksPanel";
import AuditLogPanel from "@/components/AuditLogPanel";
import IdentityPill from "@/components/IdentityPill";
import BattleInboxBadge from "@/features/battles/BattleInboxBadge";
import CostCacheDiag from "@/components/CostCacheDiag";
import AiBuddy from "@/components/AiBuddy";
import AdminFeePanel from "@/components/AdminFeePanel";
import AdminBattlesPanel from "@/components/AdminBattlesPanel";
import AdminThresholdsPanel from "@/components/AdminThresholdsPanel";
import InteractionTasksPanel from "@/components/InteractionTasksPanel";
import PlatformLLMKeysPanel from "@/components/PlatformLLMKeysPanel";
import PlatformRagPanel from "@/components/PlatformRagPanel";
import CosmicLockPanel from "@/components/CosmicLockPanel";
import PricingLab from "@/components/PricingLab";
import { AdminDfyQueue } from "@/components/DfyQueue";
import EmailQuotaCard from "@/components/EmailQuotaCard";
import CommunicationsPanel from "@/components/CommunicationsPanel";
import GeminiHealthCard from "@/components/GeminiHealthCard";
import BumpStudio from "@/components/BumpStudio";
import LLMRoutingPanel from "@/components/LLMRoutingPanel";
import { ConfirmModal } from "@/components/PromptModals";
import AdminReportsPanel from "@/components/AdminReportsPanel";
import StorageHealthPanel from "@/components/StorageHealthPanel";
import AffiliateExitEventsPanel from "@/components/AffiliateExitEventsPanel";
import StripeStatusCard from "@/components/StripeStatusCard";
import SuperAdminFinancials from "@/components/SuperAdminFinancials";
import SuperAdminPromptRegistry from "@/components/SuperAdminPromptRegistry";
import { useReviewAlerts } from "@/components/FinancialReviewBanner";
import LeaderboardsTab from "@/components/LeaderboardsTab";
import GoLivePanel from "@/components/GoLivePanel";import DangerZonePanel from "@/components/DangerZonePanel";
import MigrationPanel from "@/components/MigrationPanel";
import AdminGoogleCloudPanel from "@/components/AdminGoogleCloudPanel";
import GoogleVerificationPanel from "@/components/GoogleVerificationPanel";
import CreatorProCatalog from "@/components/CreatorProCatalog";
import KeyFailureBanner from "@/components/KeyFailureBanner";
import GrantAccessModal from "@/components/GrantAccessModal";
import CompsSubTable from "@/components/CompsSubTable";
import { Gift } from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border hairline rounded-lg p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B6A68]">
      <Icon size={14} weight="duotone" /> {label}
    </div>
    <div className="font-serif-display text-3xl mt-1 text-[#1C1B1A]">{value?.toLocaleString?.() ?? value}</div>
  </div>
);

// May 31, 2026 — shared server-side search box + pagination pills for the
// Creators and All-silos lists.
const ListSearch = ({ value, onChange, placeholder, testid }) => (
  <div className="relative w-full sm:w-80" data-testid={`${testid}-search-wrap`}>
    <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9893]" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={`${testid}-search`}
      className="w-full pl-9 pr-9 py-2 text-sm bg-white border hairline rounded-lg text-[#1C1B1A] placeholder-[#9A9893] focus:outline-none focus:border-[#C16E5A]"
    />
    {value ? (
      <button type="button" onClick={() => onChange("")} data-testid={`${testid}-search-clear`}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[#9A9893] hover:text-[#C16E5A]">
        <X size={13} />
      </button>
    ) : null}
  </div>
);

const Pager = ({ page, pages, total, pageSize, loading, onPrev, onNext, noun, testid }) => {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between gap-3 mt-3 text-sm text-[#6B6A68]" data-testid={`${testid}-pagination`}>
      <span data-testid={`${testid}-showing`}>
        {total === 0 ? `No ${noun}` : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()} ${noun}`}
      </span>
      <div className="flex items-center gap-2">
        <button type="button" disabled={page <= 1 || loading} onClick={onPrev} data-testid={`${testid}-prev`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
          <CaretLeft size={12} weight="bold" /> Prev
        </button>
        <span className="tabular-nums text-xs" data-testid={`${testid}-pageinfo`}>Page {page} / {pages}</span>
        <button type="button" disabled={page >= pages || loading} onClick={onNext} data-testid={`${testid}-next`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
          Next <CaretRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
};

// Mar 2026 — Horizontal tab scroller with desktop arrow ticks.
// Mobile users get swipe-to-scroll (CSS overflow-x-auto). Desktop mice +
// trackpads also get the swipe, but the visual affordance is the two
// tiny arrow buttons that appear only when overflow exists on that side.
const ADMIN_TABS = [
  { id: "overview", label: "Overview" },
  { id: "financials", label: "Financials" },
  { id: "platform-config", label: "Platform Config" },
  { id: "billing", label: "Billing & Earnings" },
  { id: "llm-routing", label: "LLM Routing" },
  { id: "aesthetics", label: "Bump Studio" },
  { id: "people", label: "People & Silos" },
  { id: "reports", label: "Reports" },
  { id: "comms", label: "Communications" },
  { id: "leaderboards", label: "🏆 Leaderboards" },
  { id: "go-live", label: "🚀 Go Live Wizard" },
  { id: "creator-pro", label: "Creator Pro" },
  { id: "prompt-registry", label: "Prompt Registry" },
];

function AdminTabsScroller({ adminTab, setAdminTab, reportsPending = 0, reviewCount = 0,
                            reviewUrgent = false }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const refreshArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    refreshArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshArrows, { passive: true });
    window.addEventListener("resize", refreshArrows);
    return () => {
      el.removeEventListener("scroll", refreshArrows);
      window.removeEventListener("resize", refreshArrows);
    };
  }, []);

  // Whenever the active tab changes, make sure it's scrolled into view.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector(`[data-testid="admin-tab-${adminTab}"]`);
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
    // Re-check arrows after scrollIntoView animation.
    setTimeout(refreshArrows, 350);
  }, [adminTab]);

  const scrollBy = (dx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="admin-tabs-wrap">
      {/* Left tick — only visible when there's content to scroll back to */}
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-200)}
          aria-label="Scroll tabs left"
          data-testid="admin-tabs-scroll-left"
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-5 h-7 bg-[#FDFCF8] border hairline rounded-r-md text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#4A5D4E]"
        >
          <CaretLeft size={12} weight="bold" />
        </button>
      )}
      {/* Right tick */}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollBy(200)}
          aria-label="Scroll tabs right"
          data-testid="admin-tabs-scroll-right"
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-5 h-7 bg-[#FDFCF8] border hairline rounded-l-md text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#4A5D4E]"
        >
          <CaretRight size={12} weight="bold" />
        </button>
      )}
      <div
        ref={scrollerRef}
        className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        <div className="flex items-center gap-2 min-w-max">
          {ADMIN_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setAdminTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 sm:px-5 py-3 text-sm border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0 ${adminTab === t.id ? "border-[#C16E5A] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}
            >
              {t.label}
              {/* Oct 3 2026 (Randy, audit §6.7) — money waiting on a decision,
                  or an evidence deadline running, visible without opening the
                  tab at all. */}
              {t.id === "financials" && reviewCount > 0 && (
                <span
                  data-testid="admin-tab-financials-badge"
                  className={`ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] tabular-nums align-middle ${
                    reviewUrgent ? "bg-[#C16E5A] text-white" : "bg-[#E0C79A] text-[#5A4A20]"}`}
                >
                  {reviewCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const user = getUser();
  const nav = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [silos, setSilos] = useState([]);

  // May 31, 2026 — server-side pagination + search for the Creators and
  // All-silos lists so the Super Admin page stays fast past thousands of
  // rows. 50 per page, search filters by email/name on the backend.
  const PAGE_SIZE = 50;
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPages, setUsersPages] = useState(1);
  const [usersQ, setUsersQ] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [silosTotal, setSilosTotal] = useState(0);
  const [silosPage, setSilosPage] = useState(1);
  const [silosPages, setSilosPages] = useState(1);
  const [silosQ, setSilosQ] = useState("");
  const [silosLoading, setSilosLoading] = useState(false);
  // Feb 16 2026 (Randy pt-comp-grant-system) — Grant Access modal target.
  const [grantTarget, setGrantTarget] = useState(null);
  const [compsRefresh, setCompsRefresh] = useState(0);

  // Global commands state — split per scope (iter-30). Slots drive the
  // Workbench (side-by-side comparison) and refine_silo drives the Refinery
  // (sequential per-doc distillation). They share NO commands.
  const [globalsSlots, setGlobalsSlots] = useState([]);
  const [globalsRefine, setGlobalsRefine] = useState([]);
  // Iter160c (Randy pt-ytre-p1) — YTRE nudge library. Same shape /
  // handling as slots + refine_silo. Populated on Platform-Config mount.
  const [globalsYtre, setGlobalsYtre] = useState([]);
  // Mar 2026 — Global Workbench commands section + sub-editors collapsible.
  // Default = all closed so the Platform Config tab stays scannable.
  const [openGlobalCmds, setOpenGlobalCmds] = useState(() => {
    try { return sessionStorage.getItem("admin_open_global_cmds") === "1"; } catch { return false; }
  });
  const [openSlotsCmds, setOpenSlotsCmds] = useState(() => {
    try { return sessionStorage.getItem("admin_open_slots_cmds") === "1"; } catch { return false; }
  });
  const [openRefineCmds, setOpenRefineCmds] = useState(() => {
    try { return sessionStorage.getItem("admin_open_refine_cmds") === "1"; } catch { return false; }
  });
  const [openYtreCmds, setOpenYtreCmds] = useState(() => {
    try { return sessionStorage.getItem("admin_open_ytre_cmds") === "1"; } catch { return false; }
  });
  useEffect(() => { try { sessionStorage.setItem("admin_open_global_cmds", openGlobalCmds ? "1" : "0"); } catch (e) { void e; } }, [openGlobalCmds]);
  useEffect(() => { try { sessionStorage.setItem("admin_open_slots_cmds", openSlotsCmds ? "1" : "0"); } catch (e) { void e; } }, [openSlotsCmds]);
  useEffect(() => { try { sessionStorage.setItem("admin_open_refine_cmds", openRefineCmds ? "1" : "0"); } catch (e) { void e; } }, [openRefineCmds]);
  useEffect(() => { try { sessionStorage.setItem("admin_open_ytre_cmds", openYtreCmds ? "1" : "0"); } catch (e) { void e; } }, [openYtreCmds]);
  // Tab nav (iter-29 polish) — persisted in sessionStorage so deep-linking
  // from elsewhere or a hard reload lands you back where you were.
  const [adminTab, setAdminTab] = useState(() => {
    try {
      const stored = sessionStorage.getItem("admin_tab");
      // Phase-0 migration: legacy "config" tab was split into "platform-config" + "billing".
      if (stored === "config") return "platform-config";
      return stored || "overview";
    } catch { return "overview"; }
  });
  useEffect(() => { try { sessionStorage.setItem("admin_tab", adminTab); } catch (e) { void e; } }, [adminTab]);

  // Jun 2026 — unread-reports badge on the "Reports" tab. Refetch the
  // pending count on mount and whenever the active tab changes (so it
  // drops as soon as the admin triages a report and clicks away).
  const [reportsPending, setReportsPending] = useState(0);
  // Oct 3 2026 (Randy) — financial-review badge on the Financials tab. Same
  // refetch cadence as the reports badge.
  const reviewAlerts = useReviewAlerts([adminTab]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin-reports/count");
        if (!cancelled) setReportsPending(r.data?.pending || 0);
      } catch { /* badge is decorative; ignore */ }
    })();
    return () => { cancelled = true; };
  }, [adminTab]);

  const load = async () => {
    try {
      const [a, gcSlots, gcRefine, gcYtre] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/commands?scope=slots"),
        api.get("/admin/commands?scope=refine_silo"),
        api.get("/admin/commands?scope=ytre"),
      ]);
      setAnalytics(a.data);
      setGlobalsSlots(gcSlots.data); setGlobalsRefine(gcRefine.data); setGlobalsYtre(gcYtre.data);
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      if (e.response?.status === 403) { nav("/dashboard"); return; }
      toast.error("Failed to load admin data");
    }
  };

  // Server-side paginated fetchers. Page is 1-indexed; q is the search box.
  const loadUsers = async (page = 1, q = "") => {
    setUsersLoading(true);
    try {
      const r = await api.get(`/admin/users?page=${page}&size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`);
      setUsers(r.data.users || []);
      setUsersTotal(r.data.total || 0);
      setUsersPage(r.data.page || 1);
      setUsersPages(r.data.pages || 1);
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      if (e.response?.status === 403) { nav("/dashboard"); return; }
      toast.error("Failed to load creators");
    } finally { setUsersLoading(false); }
  };
  const loadSilos = async (page = 1, q = "") => {
    setSilosLoading(true);
    try {
      const r = await api.get(`/admin/silos?page=${page}&size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`);
      setSilos(r.data.silos || []);
      setSilosTotal(r.data.total || 0);
      setSilosPage(r.data.page || 1);
      setSilosPages(r.data.pages || 1);
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      if (e.response?.status === 403) { nav("/dashboard"); return; }
      toast.error("Failed to load silos");
    } finally { setSilosLoading(false); }
  };

  useEffect(() => { load(); }, []);
  // Debounced search → reset to page 1. Fires immediately on first mount
  // (empty query, 0ms) so the lists populate, then 300ms while typing.
  useEffect(() => {
    const t = setTimeout(() => { loadUsers(1, usersQ); }, usersQ ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersQ]);
  useEffect(() => {
    const t = setTimeout(() => { loadSilos(1, silosQ); }, silosQ ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silosQ]);

  // Legacy /admin/settings endpoint deleted in Phase-0 pricing cleanup.
  // Live pricing now lives exclusively in the Billing & Earnings tab via <AdminFeePanel/>.

  // Super-admin override delete for ANY silo on the platform. Cascades the same
  // cleanup as the owner-side delete (documents, chroma, workbench state, YT lib,
  // sessions, gemini cache). usage_logs are preserved for accounting.
  // ConfirmModal state — replaces window.confirm() because the iframe sandbox
  // in the hosting preview environment silently blocks native confirm dialogs
  // (the call returns false instantly), making delete buttons appear dead.
  // Single instance pattern: { open, title, message, danger, onConfirm }.
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", danger: false, onConfirm: null });

  // Super-admin override delete for ANY user. Cascades the user + every silo
  // they own (each silo's documents/chroma/workbench-state/YT lib/sessions/cache).
  // Refuses to delete super-admins or yourself (UI button is also disabled).
  const deleteUserAsAdmin = (target) => {
    if (target.role === "super_admin" || target.id === user?.id) {
      toast.error("Super-admin accounts cannot be deleted.");
      return;
    }
    setConfirmModal({
      open: true,
      title: `Delete creator "${target.email}"?`,
      message:
        `This will cascade-wipe:\n` +
        `  • ${target.silo_count} silo${target.silo_count === 1 ? "" : "s"} (all documents + sessions + workbench state)\n` +
        `  • All YouTube libraries + Gemini caches owned by this creator\n\n` +
        `Usage history is kept for accounting. This cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        try {
          const r = await api.delete(`/admin/users/${target.id}`);
          toast.success(`Deleted "${target.email}" — ${r.data?.cascaded_silos || 0} silo(s) wiped.`);
          await loadUsers(usersPage, usersQ);
          await loadSilos(silosPage, silosQ);
          try { const a = await api.get("/admin/analytics"); setAnalytics(a.data); } catch { /* noop */ }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  // Super-admin override delete for ANY silo on the platform. Cascades the same
  // cleanup as the owner-side delete (documents, chroma, workbench state, YT lib,
  // sessions, gemini cache). usage_logs are preserved for accounting.
  const deleteSiloAsAdmin = (silo) => {
    setConfirmModal({
      open: true,
      title: `Delete "${silo.name}"?`,
      message:
        `Owner: ${silo.owner_email}\n\n` +
        `This will wipe:\n` +
        `  • ${silo.doc_count || 0} documents · ${silo.session_count || 0} chat sessions · ${silo.query_count || 0} queries\n` +
        `  • Workbench state, YouTube libraries, Gemini cache\n\n` +
        `Usage history is kept for accounting. This cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/silos/${silo.id}`);
          toast.success(`Deleted "${silo.name}".`);
          await loadSilos(silosPage, silosQ);
          try { const a = await api.get("/admin/analytics"); setAnalytics(a.data); } catch { /* noop */ }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const deleteCmdGlobal = async (cmd, scope) => {
    if (!window.confirm(`Delete global command "${cmd.name}"? This affects every creator.`)) return;
    try {
      await api.delete(`/admin/commands/${cmd.id}`);
      if (scope === "slots") setGlobalsSlots((g) => g.filter((x) => x.id !== cmd.id));
      else if (scope === "refine_silo") setGlobalsRefine((g) => g.filter((x) => x.id !== cmd.id));
      else if (scope === "ytre") setGlobalsYtre((g) => g.filter((x) => x.id !== cmd.id));
      toast.success("Deleted.");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const reloadScope = async (scope) => {
    const r = await api.get(`/admin/commands?scope=${scope}`);
    if (scope === "slots") setGlobalsSlots(r.data);
    else if (scope === "refine_silo") setGlobalsRefine(r.data);
    else if (scope === "ytre") setGlobalsYtre(r.data);
  };

  // iter-40d — Toggle silo suspension. Suspending puts the silo in read-only
  // mode (public /ask returns a polite 403 but the silo isn't deleted).
  // Pairs with admin reports — investigate first, suspend, then decide.
  const toggleSiloSuspend = async (silo) => {
    const want = !silo.is_suspended;
    let reason = "";
    if (want) {
      reason = window.prompt(
        `Suspend "${silo.name}"?\n\n` +
        `It'll stop serving /ask publicly with a polite suspension message. ` +
        `No data is deleted. You can un-suspend later.\n\n` +
        `Reason (shown to admins; not the public):`,
        "Investigation pending — flagged via reports tab."
      );
      if (reason === null) return;  // user cancelled
    } else {
      if (!window.confirm(`Un-suspend "${silo.name}"? It will resume serving /ask publicly immediately.`)) return;
    }
    try {
      await api.post(`/admin/silos/${silo.id}/suspend`, { suspended: want, reason: reason || "" });
      setSilos((prev) => prev.map((s) => s.id === silo.id ? { ...s, is_suspended: want, suspended_reason: want ? reason : null } : s));
      toast.success(want ? `"${silo.name}" suspended.` : `"${silo.name}" un-suspended.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] overflow-x-clip" data-testid="super-admin-page">
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><ArrowLeft size={18} /></Link>
            <div className="w-9 h-9 rounded-full bg-[#C16E5A] text-white flex items-center justify-center"><ShieldCheck size={18} weight="duotone" /></div>
            <div>
              <div className="font-serif-display text-xl leading-none">Super Admin</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1">Platform master dashboard</div>
            </div>
          </div>
          <span className="text-xs text-[#6B6A68] hidden lg:inline">{user?.email}</span>
          <BattleInboxBadge />
          <IdentityPill />
        </div>
      </header>

      {/* Oct 4, 2026 (Randy — "why is the Comp grants heading not there?").
          This <main> used to carry `siloart-copy`, which opts an element into
          the silo-art copy recolouring (`siloart-whitetext` / `blacktext` /
          `bold`). But this page paints its OWN opaque `bg-[#FDFCF8]` wrapper,
          so the art canvas never shows through — the copy was being recoloured
          for a background that isn't visible. With white_text on in Silo Art,
          every heading without an explicit colour rendered white on cream and
          vanished: "Platform overview", "Comp grants" and the rest were all
          still in the DOM, just invisible. A platform dashboard is not a
          creator-art surface, so it opts out entirely rather than fighting the
          recolour selector element by element. */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <KeyFailureBanner />
        <h1 className="font-serif-display text-4xl sm:text-5xl">Platform overview</h1>

        {/* Tab nav — horizontally scrollable on mobile (swipe) AND now
            on desktop via tiny left/right tick buttons. Mar 2026: phones
            already had touch-swipe; desktops with mice + trackpads need
            visible affordances since CSS doesn't expose horizontal
            scrollbars cleanly in this design. */}
        {/* Jun 2026 (Randy) — tab bar stays pinned below the header on scroll;
            the big "Platform overview" title is free to scroll away. */}
        <div className="sticky top-[68px] z-20 w-screen ml-[calc(-50vw+50%)] bg-white/90 backdrop-blur-sm border-b hairline">
          <div className="max-w-6xl mx-auto px-6 py-2">
            <AdminTabsScroller
              adminTab={adminTab} setAdminTab={setAdminTab} reportsPending={reportsPending}
              reviewCount={reviewAlerts?.needs_attention || 0}
              reviewUrgent={(reviewAlerts?.evidence_hours_left ?? 999) <= 48}
            />
          </div>
        </div>

        {/* =============================== OVERVIEW TAB =============================== */}
        <div className={adminTab === "overview" ? "" : "hidden"} data-testid="admin-pane-overview">
        {/* Live status strip — at-a-glance health of the platform's moving parts. */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="admin-live-status-strip">
          <GeminiHealthCard compact />
          <span className="text-[10px] text-[#6B6A68] uppercase tracking-[0.18em]">·</span>
          <span className="text-[10px] text-[#6B6A68]">Auto-refreshes every 5 min</span>
        </div>
        <div className="mt-6">
          <UsageOverview scope="admin" />
        </div>

        {/* Jun 2026 — cross-check engine, placed under the per-creator breakdown */}
        <div className="mt-6">
          <IntegrityChecksPanel />
        </div>

        <div className="mt-6">
          <AuditLogPanel />
        </div>

        <div className="mt-6">
          <CostCacheDiag />
        </div>

        {/* Jun 2026 (Randy) — this strip is the SITE ADMIN'S OWN AFFILIATE
            ACCOUNT snapshot (mirrors the Cost & Cache diagnostics above it,
            which is also admin-account-scoped — not a site-wide tally). */}
        <div className="mt-8" data-testid="admin-affiliate-account">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2.5 flex items-center gap-1.5">
            <Medal size={13} weight="duotone" /> Site admin&apos;s Creator Pro account
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-analytics">
            <Stat icon={Users} label="Creators referred" value={analytics?.aff_creators_referred ?? "—"} />
            <Stat icon={Medal} label="Downstream Creator Pros" value={analytics?.aff_affiliates ?? "—"} />
            <Stat icon={CurrencyDollar} label="Monthly rev from creators" value={analytics ? `$${((analytics.aff_monthly_recurring_cents || 0) / 100).toFixed(2)}` : "—"} />
            <Stat icon={Trophy} label="Gates earned to date" value={analytics ? `$${((analytics.aff_gates_earned_cents || 0) / 100).toFixed(2)}` : "—"} />
          </div>
        </div>
        </div>

        {/* =============================== FINANCIALS TAB =============================== */}
        {/* Feb 19, 2026 — SaaS-perspective P&L. Joins payment_transactions +
            affiliate_payouts + silo_subscriptions + usage_logs. Read-only. */}
        <div className={adminTab === "financials" ? "" : "hidden"} data-testid="admin-pane-financials">
          {adminTab === "financials" && <SuperAdminFinancials />}
        </div>

        {/* =============================== PLATFORM CONFIG TAB =============================== */}
        {/* Feature thresholds, email toggles, and global workbench commands.
            All money/pricing/earnings dials live in the Billing & Earnings tab. */}
        <div className={adminTab === "platform-config" ? "" : "hidden"} data-testid="admin-pane-platform-config">

        {/* Iter160v (Randy pt-cosmic-lock, Feb 2026) — Cosmic Lock sits
            at the VERY TOP of Platform Config. Controls whether the
            landing page's "See a live silo" button demos Cosmic
            (locked, default ON) or organically rotates across active
            creators (unlocked). Placement is deliberate: this is the
            single most user-facing platform behavior a super admin can
            flip, so it lives above every other config knob. */}
        <section className="mt-0 mb-8" data-testid="admin-cosmic-lock-section">
          <CosmicLockPanel />
        </section>

        {/* Feb 2026 (Randy pt-rag-lift) — Retrieval Diversity (MMR) sits at
            the TOP of Platform Config because retrieval quality is the
            foundation of every answer the platform produces (Public QA,
            YT Replies, AI Assistant, Battle, Probe). Any change here
            ripples across the entire platform. Panel is self-documenting
            — long-form explainer lives in-component so it's read before
            it's touched. */}
        <section className="mt-0 mb-8" data-testid="admin-platform-rag-section">
          <PlatformRagPanel />
        </section>

        {/* Stripe Express ("bank of record") — moved here Jun 2026 (Randy): it's
            platform configuration, not an at-a-glance overview metric. Pinned to
            the top of Platform Config because EVERY fan payment is gated on the
            owner's stripe_charges_enabled. */}
        <div className="mb-8" data-testid="admin-stripe-express-config">
          <StripeStatusCard />
        </div>

        {/* Platform LLM Keys panel. Pinned to the TOP of Platform Config
            because direct provider keys are the highest-leverage cost
            lever on the platform — the 3× markup applied on top becomes
            pure platform margin. */}
        <section className="mt-0 mb-12" data-testid="admin-platform-llm-keys-section">
          <PlatformLLMKeysPanel />
        </section>

        {/* Platform thresholds (iter-26) ================================== */}
        <section className="mt-0" data-testid="admin-thresholds-section">
          <AdminThresholdsPanel />
        </section>

        {/* Global Workbench Commands (iter-30) — split per scope.
              Slots: for side-by-side comparison in the Workbench.
              Refine Silo: for sequential per-doc distillation in the Refinery.
              Mar 2026: collapsible (parent + each sub-editor) to reduce
              Platform Config scroll noise. */}
        <section className="mt-12" data-testid="admin-global-commands-section">
          <button
            type="button"
            onClick={() => setOpenGlobalCmds((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left py-2 border-b hairline hover:border-[#4A5D4E] transition-colors"
            data-testid="global-cmds-toggle"
            aria-expanded={openGlobalCmds}
          >
            <h2 className="font-serif-display text-2xl flex items-center gap-2">
              <Wrench size={20} weight="duotone" /> Global Workbench commands
            </h2>
            {openGlobalCmds
              ? <CaretDown size={20} weight="bold" className="text-[#6B6A68] shrink-0" />
              : <CaretRight size={20} weight="bold" className="text-[#6B6A68] shrink-0" />}
          </button>

          {openGlobalCmds && (
            <div className="mt-4">
              <p className="text-sm text-[#6B6A68] mb-4 max-w-3xl">
                These appear under "Global (built-in)" in every creator's command picker. Slots and
                Refine Silo use <em>different libraries</em> because they do different work:
                Slots compares up to 3 chunks in one AI call (relational), Refine Silo processes
                one chunk from one doc at a time (sequential). Edit each set independently.
              </p>

              <div className="border hairline rounded-md bg-white" data-testid="slots-cmds-collapsible">
                <button
                  type="button"
                  onClick={() => setOpenSlotsCmds((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 hover:bg-[#F7F4EE] transition-colors"
                  data-testid="slots-cmds-toggle"
                  aria-expanded={openSlotsCmds}
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-serif-display text-base text-[#1C1B1A]">Slots — Global Workbench commands</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] shrink-0">{globalsSlots.length} cmd{globalsSlots.length === 1 ? "" : "s"}</span>
                  </div>
                  {openSlotsCmds
                    ? <CaretDown size={16} weight="bold" className="text-[#6B6A68] shrink-0" />
                    : <CaretRight size={16} weight="bold" className="text-[#6B6A68] shrink-0" />}
                </button>
                {openSlotsCmds && (
                  <div className="px-4 pb-4">
                    <ScopedGlobalCommandEditor
                      scope="slots"
                      title="Slots — Global Workbench commands"
                      subtitle="Shown in the Workbench slot-operation dropdown. Best for compare/contrast/cross-ref across 1–3 slots."
                      items={globalsSlots}
                      onDelete={(cmd) => deleteCmdGlobal(cmd, "slots")}
                      onReload={() => reloadScope("slots")}
                      aiTitle="Slot — AI Command Assist"
                      aiSubtitle="Command-drafting helper for the Slots library"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 border hairline rounded-md bg-white" data-testid="refine-cmds-collapsible">
                <button
                  type="button"
                  onClick={() => setOpenRefineCmds((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 hover:bg-[#F7F4EE] transition-colors"
                  data-testid="refine-cmds-toggle"
                  aria-expanded={openRefineCmds}
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-serif-display text-base text-[#1C1B1A]">Refine Silo — Global Workbench commands</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] shrink-0">{globalsRefine.length} cmd{globalsRefine.length === 1 ? "" : "s"}</span>
                  </div>
                  {openRefineCmds
                    ? <CaretDown size={16} weight="bold" className="text-[#6B6A68] shrink-0" />
                    : <CaretRight size={16} weight="bold" className="text-[#6B6A68] shrink-0" />}
                </button>
                {openRefineCmds && (
                  <div className="px-4 pb-4">
                    <ScopedGlobalCommandEditor
                      scope="refine_silo"
                      title="Refine Silo — Global Workbench commands"
                      subtitle="Shown in the Refinery operation dropdown. Best for bulk distillation / extraction across an entire tier."
                      items={globalsRefine}
                      onDelete={(cmd) => deleteCmdGlobal(cmd, "refine_silo")}
                      onReload={() => reloadScope("refine_silo")}
                      aiTitle="Refine Silo — AI Command Assist"
                      aiSubtitle="Command-drafting helper for the Refine Silo library"
                    />
                  </div>
                )}
              </div>

              {/* Iter160c (Randy pt-ytre-p1) — YTRE nudge library. Uses
                  the same shared /admin/commands infrastructure as slots
                  and refine_silo, just with scope="ytre". These commands
                  are STANCE modifiers on the silo's Q&A pipeline when
                  answering a transcript slice — Pure silo, Reject, Rebuttal,
                  Agree, Compliment (seeded on boot). Admins can add more
                  at the platform level; end users create per-user customs
                  from inside YTRE via the "+ Create custom nudge" button. */}
              <div className="border hairline rounded-lg overflow-hidden" data-testid="admin-global-cmds-ytre-wrap">
                <button
                  type="button"
                  onClick={() => setOpenYtreCmds((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 hover:bg-[#F7F4EE] transition-colors"
                  data-testid="ytre-cmds-toggle"
                  aria-expanded={openYtreCmds}
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-serif-display text-base text-[#1C1B1A]">YTRE — Global Commands</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] shrink-0">{globalsYtre.length} cmd{globalsYtre.length === 1 ? "" : "s"}</span>
                  </div>
                  {openYtreCmds
                    ? <CaretDown size={16} weight="bold" className="text-[#6B6A68] shrink-0" />
                    : <CaretRight size={16} weight="bold" className="text-[#6B6A68] shrink-0" />}
                </button>
                {openYtreCmds && (
                  <div className="px-4 pb-4">
                    <ScopedGlobalCommandEditor
                      scope="ytre"
                      title="YTRE — Global Commands"
                      subtitle="Nudges that guide the silo's (Tier 1/2/3 · RAG · vector · public-QA) pipeline when answering a transcript slice pulled from a YouTube video. Same infra as Slots + Refine Silo — creators see these in YTRE's 'Ask silo' dropdown."
                      items={globalsYtre}
                      onDelete={(cmd) => deleteCmdGlobal(cmd, "ytre")}
                      onReload={() => reloadScope("ytre")}
                      aiTitle="YTRE — AI Nudge Assist"
                      aiSubtitle="Command-drafting helper for YTRE stances — describe how you want the silo to react to a video segment."
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Jun 8, 2026 — Visitor billing ("dots") editor. Bottom of Platform
            Config; site-wide for now (Randy). */}
        <section className="mt-12" data-testid="admin-interaction-tasks-wrap">
          {adminTab === "platform-config" && <InteractionTasksPanel />}
        </section>
        </div>

        {/* =============================== BILLING & EARNINGS TAB =============================== */}
        {/* Single source of truth for every monetary dial on the platform.
            Sub-section A: Billing & Earnings (live money dials — PricingLab).
            Sub-section B: Per-creator overrides (AdminFeePanel). */}
        <div className={adminTab === "billing" ? "" : "hidden"} data-testid="admin-pane-billing">

        {/* A) Billing & Earnings — the single live editor for every money dial.
              PricingLab renders its own header + description; no outer wrapper
              copy so we don't reintroduce phantom "sandbox/projection" language. */}
        <section className="mt-0" data-testid="admin-pricing-lab-section">
          {adminTab === "billing" && <PricingLab />}
        </section>

        {/* B) Live AdminFeePanel — per-creator overrides only (Feb 19, 2026:
              the duplicate global-fee block lived here too. Removed — global
              dials live exclusively in <PricingLab/> above.) */}
        <section className="mt-12" data-testid="admin-stripe-fees-section">
          <h2 className="font-serif-display text-2xl mb-3 flex items-center gap-2">
            <Percent size={20} weight="duotone" /> Per-creator overrides
          </h2>
          <p className="text-sm text-[#6B6A68] mb-4 max-w-3xl">
            Bespoke pricing for individual creators. Search by email or name. Lock any override
            so it survives future global-default bulk updates.
          </p>
          <AdminFeePanel />
        </section>

        {/* Silo Wars master toggle (add-on, Feb 12, 2026) */}
        <section className="mt-12" data-testid="admin-battles-section">
          <h3 className="font-serif-display text-2xl mb-2">Silo Wars (add-on)</h3>
          <p className="text-sm text-[#6B6A68] mb-4 max-w-2xl">
            Master switch for the Silo Wars feature. Off by default —
            decide if you're launching with battles enabled.
          </p>
          <AdminBattlesPanel />
        </section>

        {/* C) DFY Queue — Phase 4 */}
        {adminTab === "billing" && (
          <section className="mt-12" data-testid="admin-dfy-section">
            <AdminDfyQueue />
          </section>
        )}

        </div>

        {/* =============================== LLM ROUTING TAB =============================== */}
        <div className={adminTab === "llm-routing" ? "" : "hidden"} data-testid="admin-pane-llm-routing">
          {adminTab === "llm-routing" && <LLMRoutingPanel />}
        </div>

        {/* =============================== SITE AESTHETICS TAB =============================== */}
        <div className={adminTab === "aesthetics" ? "" : "hidden"} data-testid="admin-pane-aesthetics">
          {adminTab === "aesthetics" && <BumpStudio />}
        </div>

        {/* =============================== PEOPLE & SILOS TAB =============================== */}
        <div className={adminTab === "people" ? "" : "hidden"} data-testid="admin-pane-people">

        {/* Users */}
        <section className="mt-0">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-serif-display text-2xl">Creators</h2>
            <span className="text-xs text-[#6B6A68]">Click <Trash size={11} className="inline mb-0.5" /> to delete a creator. Cascade-wipes ALL their silos, documents, sessions, workbench state, YouTube libraries, and Gemini caches. Super-admins cannot be deleted.</span>
          </div>
          <div className="mb-3">
            <ListSearch value={usersQ} onChange={setUsersQ} placeholder="Search creators by email or name…" testid="admin-users" />
          </div>
          <div className="bg-white border hairline rounded-lg overflow-x-auto" data-testid="admin-users">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-right px-4 py-3">Silos</th>
                  <th className="text-right px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const protectedAcct = u.role === "super_admin" || u.id === user?.id;
                  return (
                    <tr key={u.id} className="border-t hairline" data-testid={`admin-user-row-${u.email}`}>
                      <td className="px-4 py-3 text-[#1C1B1A]">{u.email}</td>
                      <td className="px-4 py-3 text-[#6B6A68]">{u.name}</td>
                      <td className="px-4 py-3">
                        {/* Sprint Feb 8, 2026 — use the canonical derived_role
                            from the backend (single source of truth: same
                            logic that powers the Identity Pill, the per-
                            creator breakdown, and the dashboard gate). */}
                        {(() => {
                          const dr = u.derived_role || u.role;
                          const TONES = {
                            super_admin: "bg-[#FBE9E1] text-[#7A3A2A]",
                            affiliate_creator: "bg-[#E7E1F5] text-[#4B3A7A]",
                            creator: "bg-[#E8EFE9] text-[#3A4F40]",
                            new_creator: "bg-[#F4EEDC] text-[#6B5A2D]",
                            fan: "bg-[#F0F1EE] text-[#6B6A68]",
                          };
                          const LABELS = {
                            super_admin: "Super Admin",
                            affiliate_creator: "Creator Pro",
                            creator: "Creator",
                            new_creator: "New Creator",
                            fan: "Fan",
                          };
                          return (
                            <span
                              data-testid={`role-badge-${dr}-${u.id}`}
                              className={`text-xs px-2 py-1 rounded ${TONES[dr] || TONES.fan}`}
                            >
                              {LABELS[dr] || dr}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{u.silo_count}</td>
                      <td className="px-2 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setGrantTarget(u)}
                          data-testid={`admin-user-grant-${u.email}`}
                          aria-label={`Grant free access to ${u.email}`}
                          title={`Grant free access to "${u.email}"`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#E8EFE9]"
                        >
                          <Gift size={14} weight="duotone" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteUserAsAdmin(u)}
                          disabled={protectedAcct}
                          data-testid={`admin-user-delete-${u.email}`}
                          title={protectedAcct ? "Super-admin accounts cannot be deleted" : `Delete "${u.email}" + cascade ${u.silo_count} silos`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FDF1ED] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#6B6A68]"
                        >
                          <Trash size={14} weight="duotone" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6B6A68] text-sm">{usersLoading ? "Loading…" : usersQ ? "No creators match your search." : "No creators yet."}</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager page={usersPage} pages={usersPages} total={usersTotal} pageSize={PAGE_SIZE} loading={usersLoading}
            onPrev={() => loadUsers(usersPage - 1, usersQ)} onNext={() => loadUsers(usersPage + 1, usersQ)}
            noun="creators" testid="admin-users" />
        </section>

        {/* Silos */}
        <section className="mt-12 mb-16">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-serif-display text-2xl">All silos</h2>
            <span className="text-xs text-[#6B6A68]">Click <Trash size={11} className="inline mb-0.5" /> to delete a silo. Cascade-wipes its documents, sessions, workbench state, YouTube libraries, and Gemini cache. Usage history is preserved for accounting.</span>
          </div>
          <div className="mb-3">
            <ListSearch value={silosQ} onChange={setSilosQ} placeholder="Search silos by name, slug, or owner…" testid="admin-silos" />
          </div>
          <div className="bg-white border hairline rounded-lg overflow-x-auto" data-testid="admin-silos">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Slug</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Template</th>
                  <th className="text-right px-4 py-3">Docs</th>
                  <th className="text-right px-4 py-3">Sessions</th>
                  <th className="text-right px-4 py-3">Queries</th>
                  <th className="text-center px-4 py-3 w-24">Status</th>
                  <th className="text-right px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {silos.map((s) => (
                  <tr key={s.id} className={`border-t hairline ${s.is_suspended ? "bg-[#FFF7F1]" : ""}`} data-testid={`admin-silo-row-${s.slug}`}>
                    <td className="px-4 py-3 text-[#1C1B1A]">{s.name}</td>
                    <td className="px-4 py-3 text-[#6B6A68] font-mono text-xs">/s/{s.slug}</td>
                    <td className="px-4 py-3 text-[#6B6A68]">{s.owner_email}</td>
                    <td className="px-4 py-3 text-[#6B6A68]">{s.template}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.doc_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.session_count ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.query_count}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggleSiloSuspend(s)}
                        data-testid={`admin-silo-suspend-${s.slug}`}
                        title={s.is_suspended
                          ? `Suspended${s.suspended_reason ? `: ${s.suspended_reason}` : ""}. Click to un-suspend.`
                          : "Click to suspend (puts silo in read-only mode; no data deleted)"}
                        className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded font-medium border hairline ${
                          s.is_suspended
                            ? "bg-[#FCEDE6] text-[#A85E4A] border-[#C16E5A]/30 hover:bg-[#F8DCD0]"
                            : "bg-[#E8F0E9] text-[#2F6B3A] border-[#2F6B3A]/20 hover:bg-[#D6E5D8]"
                        }`}
                      >
                        {s.is_suspended ? "Suspended" : "Live"}
                      </button>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => deleteSiloAsAdmin(s)}
                        data-testid={`admin-silo-delete-${s.slug}`}
                        title={`Delete "${s.name}" (${s.doc_count} docs · ${s.session_count ?? 0} sessions)`}
                        className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FDF1ED]"
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    </td>
                  </tr>
                ))}
                {silos.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#6B6A68] text-sm">{silosLoading ? "Loading…" : silosQ ? "No silos match your search." : "No silos yet."}</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager page={silosPage} pages={silosPages} total={silosTotal} pageSize={PAGE_SIZE} loading={silosLoading}
            onPrev={() => loadSilos(silosPage - 1, silosQ)} onNext={() => loadSilos(silosPage + 1, silosQ)}
            noun="silos" testid="admin-silos" />
        </section>

        {/* Feb 16 2026 (Randy pt-comp-grant-system) — Comp grants audit table
            at the bottom of People and Silos. Filter/search/toggle/revoke. */}
        <CompsSubTable onGrant={(u) => setGrantTarget(u)} refreshKey={compsRefresh} />
        </div>

        {/* =============================== REPORTS TAB =============================== */}
        {/* Promoted to its own tab in the Feb 7 sprint. Houses both the
            user-submitted Reports/Contact triage and the Affiliate exit
            events audit trail. */}
        <div className={adminTab === "reports" ? "" : "hidden"} data-testid="admin-pane-reports">
          <section className="mb-8" data-testid="admin-reports-section">
            <AdminReportsPanel />
          </section>
          <section className="mb-8" data-testid="admin-storage-health-section">
            <StorageHealthPanel />
          </section>
          <section data-testid="admin-affiliate-exit-events-section">
            <AffiliateExitEventsPanel />
          </section>
        </div>

        {/* =============================== COMMUNICATIONS TAB =============================== */}
        {/* Feb 7 sprint fix — pane was previously empty (component imported
            but never rendered). Wired in CommunicationsPanel with the admin
            scope so super admins get the platform-wide audience picker. */}
        <div className={adminTab === "comms" ? "" : "hidden"} data-testid="admin-pane-comms">
          {adminTab === "comms" && <CommunicationsPanel isAdmin />}
        </div>

        {/* =============================== LEADERBOARDS TAB =============================== */}
        <div className={adminTab === "leaderboards" ? "" : "hidden"} data-testid="admin-pane-leaderboards">
          {adminTab === "leaderboards" && <LeaderboardsTab />}
        </div>

        {/* =============================== GO LIVE WIZARD TAB =============================== */}
        {/* Jun 2026 (Randy) — relocated the Go-Live wizard + Danger Zone out of
            Financials into their own tab, and added the Export/Import data
            migration tool. Keeps the money screens clean. */}
        <div className={adminTab === "go-live" ? "" : "hidden"} data-testid="admin-pane-go-live">
          {adminTab === "go-live" && (
            <div className="space-y-8">
              <AdminGoogleCloudPanel />
              <GoogleVerificationPanel />
              <MigrationPanel />
              <GoLivePanel />
              <DangerZonePanel />
            </div>
          )}
        </div>
        <div className={adminTab === "creator-pro" ? "" : "hidden"} data-testid="admin-pane-creator-pro">
          {adminTab === "creator-pro" && <CreatorProCatalog />}
        </div>

        {/* Jun 2026 (PIPELINE_SIMPLIFICATION_PROJECT.md item E) — Randy:
            "it would be nice if we built a tab in the super admin that gives a
            home for all these rules… this would help me see, if im over
            engineering, creating bloat, or context confusion etc." */}
        <div className={adminTab === "prompt-registry" ? "" : "hidden"} data-testid="admin-pane-prompt-registry">
          {adminTab === "prompt-registry" && <SuperAdminPromptRegistry />}
        </div>
      </main>

      {/* In-app confirmation dialog (replaces window.confirm which the iframe sandbox blocks) */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="Delete"
        danger={confirmModal.danger}
        onConfirm={() => { confirmModal.onConfirm?.(); }}
        onClose={() => setConfirmModal((c) => ({ ...c, open: false }))}
      />
      {/* Feb 16 2026 (Randy pt-comp-grant-system) — Grant Access modal
          mounted at SuperAdmin root so it overlays the People tab. */}
      {grantTarget && (
        <GrantAccessModal
          targetUser={grantTarget}
          onClose={() => setGrantTarget(null)}
          onGranted={() => { setGrantTarget(null); loadUsers(usersPage, usersQ); setCompsRefresh((n) => n + 1); }}
        />
      )}
    </div>
  );
}

// Email deliverability probe — hits /api/admin/email-test and reports success/failure.
// Helps diagnose Resend password-reset issues reported in iter-21.
function EmailDeliveryTestCard() {
  const user = getUser();
  const [to, setTo] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, provider_response, sender, to }

  const send = async () => {
    if (!to.trim()) return toast.error("Recipient email is required.");
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/admin/email-test", { to });
      setResult(r.data);
      if (r.data.ok) toast.success(`Test email accepted by Resend → ${to}`);
      else toast.error(r.data.error || "Resend rejected the email.");
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || "Request failed." });
      toast.error(e.response?.data?.detail || "Request failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border hairline rounded-lg p-6" data-testid="admin-email-test-card">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-sm text-[#6B6A68] mb-1.5">Send test to</label>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="admin-email-test-to"
            className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            placeholder="you@example.com" />
        </div>
        <button onClick={send} disabled={sending} data-testid="admin-email-test-send"
          className="px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
          {sending ? "Sending…" : "Send test email"}
        </button>
      </div>
      {result && (
        <div className="mt-4 p-3 rounded-md text-xs font-mono whitespace-pre-wrap break-all bg-[#F0F1EE] text-[#1C1B1A]"
          data-testid="admin-email-test-result">
          <div className={result.ok ? "text-[#2F6B3A]" : "text-[#C16E5A]"}>{result.ok ? "✓ success" : "✗ failure"}</div>
          <div>sender: {result.sender}</div>
          <div>to: {result.to}</div>
          {result.resend_id && <div>resend id: {result.resend_id}</div>}
          {result.error && <div>error: {result.error}</div>}
          {!result.sender_domain_verified && (
            <div className="mt-2 text-[#8A5A00]">
              Note: you are using Resend's shared sandbox domain. It only delivers to addresses
              verified on your Resend account. Verify a custom domain for production.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Reusable admin editor for ONE scope of global commands (slots or refine_silo).
// Each instance owns its own draft/edit state so the two editors don't interfere.
// iter-30.
function ScopedGlobalCommandEditor({ scope, title, subtitle, items, onDelete, onReload, aiTitle, aiSubtitle }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", instructions: "", default_output_pct: null });
  const [saving, setSaving] = useState(false);

  const startEdit = (cmd) => { setEditingId(cmd.id); setDraft({ name: cmd.name, instructions: cmd.instructions, default_output_pct: cmd.default_output_pct ?? null }); };
  const cancelEdit = () => { setEditingId(null); setDraft({ name: "", instructions: "", default_output_pct: null }); };
  const reset = () => { setEditingId(null); setDraft({ name: "", instructions: "", default_output_pct: null }); };

  const save = async () => {
    if (!draft.name.trim() || !draft.instructions.trim()) return toast.error("Name and instructions required.");
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/admin/commands/${editingId}`, draft);
        toast.success(`Global command updated (${scope}).`);
      } else {
        await api.post("/admin/commands", { ...draft, scope });
        toast.success(`Global command created (${scope}).`);
      }
      reset();
      await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save command");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`admin-global-cmd-editor-${scope}`}>
      <div className="mb-2">
        <h3 className="font-serif-display text-xl text-[#1C1B1A]">{title}</h3>
        <p className="text-xs text-[#6B6A68] mt-1 max-w-3xl">{subtitle}</p>
      </div>
      <div className="bg-white border hairline rounded-lg overflow-hidden" data-testid={`admin-global-commands-${scope}`}>
        <ul className="divide-y hairline">
          {items.length === 0 && (
            <li className="p-6 text-center text-sm text-[#6B6A68]">No global commands in this scope yet. Add one below.</li>
          )}
          {items.map((c, idx) => (
            <li key={c.id} className={`p-4 flex items-start gap-3 ${editingId === c.id ? "bg-[#F0F1EE]" : ""}`} data-testid={`global-cmd-row-${scope}-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2">
                  {idx === 0 && (
                    <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#2F6B3A] text-white font-medium">
                      Default
                    </span>
                  )}
                  {c.name}
                  <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] font-medium" data-testid={`global-cmd-size-${scope}-${c.id}`}>
                    {c.default_output_pct == null ? "Auto" : `~${c.default_output_pct}%`}
                  </span>
                </div>
                <div className="text-xs text-[#6B6A68] mt-1 line-clamp-3 whitespace-pre-wrap">{c.instructions}</div>
              </div>
              {idx > 0 && (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/admin/commands/${c.id}/move-to-top`);
                      toast.success(`Moved "${c.name}" to top.`);
                      await onReload();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Could not reorder.");
                    }
                  }}
                  data-testid={`global-cmd-move-top-${scope}-${c.id}`}
                  className="p-2 rounded-md text-[#6B6A68] hover:text-[#2F6B3A] hover:bg-white"
                  title="Move to top (becomes the default everyone sees first)"
                >
                  <ArrowFatLineUp size={16} weight="duotone" />
                </button>
              )}
              <button onClick={() => startEdit(c)} data-testid={`global-cmd-edit-${scope}-${c.id}`}
                className="p-2 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-white" title="Edit">
                <PencilSimple size={16} weight="duotone" />
              </button>
              <button onClick={() => { onDelete(c); if (editingId === c.id) cancelEdit(); }} data-testid={`global-cmd-delete-${scope}-${c.id}`}
                className="p-2 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-white" title="Delete">
                <Trash size={16} weight="duotone" />
              </button>
            </li>
          ))}
        </ul>
        <div className="border-t hairline p-5 bg-[#FDFCF8]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1.5">
              {editingId ? <><PencilSimple size={12} /> Editing existing</> : <><Plus size={12} /> Add a new global command</>}
            </div>
            {editingId && (
              <button onClick={cancelEdit} data-testid={`global-cmd-cancel-${scope}`}
                className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1">
                <X size={12} /> Cancel edit
              </button>
            )}
          </div>
          <label className="block text-sm text-[#6B6A68] mb-1.5">Name</label>
          <input data-testid={`global-cmd-name-${scope}`} value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            placeholder={scope === "slots" ? "e.g., Compare drafts — strongest thesis" : "e.g., Extract axioms"} />
          <label className="block text-sm text-[#6B6A68] mt-3 mb-1.5">Instructions</label>
          <textarea data-testid={`global-cmd-instructions-${scope}`} rows={4} value={draft.instructions}
            onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
            className="w-full bg-white border hairline rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            placeholder={scope === "slots"
              ? "Describe exactly what the AI should do across the slot inputs (it sees up to 3 chunks at once)…"
              : "Describe exactly what the AI should do with ONE chunk of ONE document (it processes them sequentially)…"} />
          <label className="block text-sm text-[#6B6A68] mt-3 mb-1.5">Default output size</label>
          <div className="relative">
            <select
              data-testid={`global-cmd-output-pct-${scope}`}
              value={draft.default_output_pct == null ? "" : String(draft.default_output_pct)}
              onChange={(e) => setDraft({ ...draft, default_output_pct: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
              className="appearance-none w-full bg-white border hairline rounded-md px-4 py-2.5 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
              <option value="">Auto · model decides (best for analysis / grammar)</option>
              {[10, 15, 20, 25, 30, 40, 50, 60, 75, 90, 100].map((p) => (
                <option key={p} value={p}>~{p}% of the input</option>
              ))}
            </select>
            <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
          </div>
          <p className="text-[11px] text-[#9A9892] mt-1">Pre-sets the Workbench output-size slider for every creator when this command is picked. Auto means no size hint, so it will not shrink a grammar or proofread pass.</p>
          <div className="mt-3 flex justify-end gap-2">
            {!editingId && (draft.name || draft.instructions) && (
              <button onClick={reset} type="button" className="px-3 py-2 text-sm text-[#6B6A68] hover:text-[#1C1B1A]">Reset</button>
            )}
            <button onClick={save} disabled={saving} data-testid={`global-cmd-save-${scope}`}
              className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60 flex items-center gap-1.5">
              <FloppyDisk size={14} /> {saving ? "Saving…" : (editingId ? "Save changes" : "Add command")}
            </button>
          </div>
          <div className="mt-5" data-testid={`ai-buddy-mount-admin-commands-${scope}`}>
            <AiBuddy
              surface={`admin-global-commands-${scope}`}
              compact
              title={aiTitle}
              subtitle={aiSubtitle}
              emptyHint={scope === "slots"
                ? "Tell Slot Command Assist your comparison or cross-ref intention — it will draft a short, accurate command for the Workbench."
                : "Tell Refine Silo Command Assist what you want to extract or distill across docs — it will draft a short, accurate per-chunk command."}
              onPasteToInstructions={(text) => {
                setDraft((d) => ({ ...d, instructions: text }));
                toast.success("Pasted into Instructions field.");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

